import 'core-js/stable';
import 'bootstrap';
import React from 'react';
import { AppRoutes } from 'routes';
import { BrowserRouter } from 'react-router-dom';
import { configureRum } from 'config/rum';
import { createRoot } from 'react-dom/client';
import { DebouncerProvider } from 'components/common/DebouncerProvider';
import { ErrorBoundary } from 'components/common/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider, LanguageRouteSync } from 'config/i18n';
import { Environment, getEnvironment } from 'config/constants';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MaterialUiProvider } from 'components/common/MaterialUiProvider';

import './css/bootstrapTheming.scss';
import './css/site.scss';
import 'react-loading-skeleton/dist/skeleton.css';


const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 60 * 1000, // reload data after 1 hour
            gcTime: 60 * 60 * 1000, // remove data by GC if data unused/inactive more than 1 hour
        },
    },
});

const isDev = getEnvironment() === Environment.Local || Environment.Development;

async function start() {
    const App = () => (
        <QueryClientProvider client={queryClient}>
            <MaterialUiProvider>
                <DebouncerProvider>
                    <I18nextProvider>
                        <ErrorBoundary>
                            <BrowserRouter>
                                <LanguageRouteSync />
                                <AppRoutes />
                                {isDev && <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />}
                            </BrowserRouter>
                        </ErrorBoundary>
                    </I18nextProvider>
                </DebouncerProvider>
            </MaterialUiProvider>
        </QueryClientProvider>
    );

    const renderApp = () => {
        const container = document.getElementById('root');

        const root = createRoot(container!);
        root.render(App());
        const appInitLoader = document.getElementById('app-init-loader');
        if (appInitLoader) {
            appInitLoader.remove();
        }
    };

    configureRum();

    renderApp();
}

start();
